import { observer } from 'mobx-react-lite';
import { withDefaultProps } from 'react-default-props-context';
import { AdView } from 'src/ads/components/AdView';
import { ExplanationView } from 'src/answers/components/ExplanationView';
import { ExplanationT } from 'src/api/types/ExplanationT';
import { QuestionT } from 'src/api/types/QuestionT';
import { SelfTestT } from 'src/api/types/SelfTestT';
import { cn } from 'src/frames/utils/cn';
import { QuestionView } from 'src/questions/components';
import { LandingView } from 'src/selfTest/components';
import { SummaryView } from 'src/summary/components';
import { getUrlParam } from 'src/utils/getUrlParam';
import './SelfTestView.scss';
import * as React from 'react';
import { initCMP } from '../../consent/utils/initCMP';
import { useRef } from 'react';
import { flags } from '../../app/flags';
import { initAds } from '../../ads/utils/initAds';
import { initTracking } from '../../tracking/utils/initTracking';
import { TrackingConfig } from '../../tracking/types';
import {AdSlot} from "../../ads/components/AdSlot";

type PropsT = {
  className?: any;
};

type DefaultPropsT = {
  ad: boolean;
  question: QuestionT;
  explanation: ExplanationT;
  pageIdx: number;
  selfTest: SelfTestT;
  selfTestRS: string;
  setPortalElm: (elm: any) => void;
};

export const SelfTestView = observer(
  withDefaultProps<PropsT, DefaultPropsT>((props: PropsT & DefaultPropsT) => {
    const hasConnectionError =
      props.selfTestRS === 'success' && !props.selfTest;
    const showConnectionError = !!getUrlParam('edit');

    const ref = useRef(false);
    const showAds =
      flags.enableAds && getUrlParam('showAds') === '1' && !!getUrlParam('csid');
    const enableTracking =
      flags.enableTracking && getUrlParam('enableTracking') === '1';

    // Make sure that the counting ad is initialized after the `setGlobalOptions` is pushed to `_aeq`
    const [showCountingAd, setShowCountingAd] = React.useState(false);
    const [cpUserAuthenticatedValue, setCpUserAuthenticatedValue] = React.useState<boolean | null>(null);
    const [isAdVisible, setIsAdVisible] = React.useState(showAds);
    const [adIsInitialized, setAdIsInitialized] = React.useState(false);

    React.useEffect(() => {
      const handleAuthenticationStatus = (event: MessageEvent) => {
        const origins = [
          window.location.protocol + "//" + window.location.host,
          // ND
          'https://www.netdoktor.de',
          'https://stage.netdoktor.de',
          'https://stage.tech.netdoktor.de',
          'https://www.netdoktor.at',
          'https://stage.netdoktor.at',
          'https://stage.tech.netdoktor.at',
          'https://www.netdoktor.ch',
          'https://stage.netdoktor.ch',
          'https://stage.tech.netdoktor.ch',
          // GPP; They are commented as for now there is no Contentpass for GPP
          // 'https://www.gesundheitsportal-privat.de',
          // 'https://gesundheitsportal-privat.de',
          // 'https://dev.tech.gesundheitsportal-privat.de',
          // 'https://signal-iduna.gesundheitsportal-privat.de',
          // 'https://signal-iduna.dev.tech.gesundheitsportal-privat.de',
          // 'https://muenchener-verein.gesundheitsportal-privat.de',
          // 'https://muenchener-verein.dev.tech.gesundheitsportal-privat.de',
          // 'https://lvm.gesundheitsportal-privat.de',
          // 'https://lvm.dev.tech.gesundheitsportal-privat.de',
          // 'https://huk-coburg.gesundheitsportal-privat.de',
          // 'https://huk-coburg.dev.tech.gesundheitsportal-privat.de',
          // 'https://hansemerkur.gesundheitsportal-privat.de',
          // 'https://hansemerkur.dev.tech.gesundheitsportal-privat.de',
          // 'https://hallesche.gesundheitsportal-privat.de',
          // 'https://hallesche.dev.tech.gesundheitsportal-privat.de',
          // 'https://debeka.gesundheitsportal-privat.de',
          // 'https://debeka.dev.tech.gesundheitsportal-privat.de',
          // 'https://continentale.gesundheitsportal-privat.de',
          // 'https://continentale.dev.tech.gesundheitsportal-privat.de',
          // 'https://alte-oldenburger.gesundheitsportal-privat.de',
          // 'https://alte-oldenburger.dev.tech.gesundheitsportal-privat.de',
        ];

        if (
          origins.indexOf(event.origin) !== -1 &&
          event?.data?.type === 'cpUserAuthenticated'
        ) {
          setCpUserAuthenticatedValue(event.data.value);
          if (event.data.value) {
            setIsAdVisible(false);
          }

          window.removeEventListener('message', handleAuthenticationStatus);
        }
      };

      window.addEventListener('message', handleAuthenticationStatus);

      // Request the contentpass authentication status in case the `cpUserAuthenticated` message was dispatched before the listener
      window.parent.postMessage({type: 'cpAuthenticationStatusRequest'}, '*');
    }, []);

    React.useEffect(() => {
      if (props.selfTest && !ref.current) {
        ref.current = true;

        // CMP is required for either ads or tracking
        if (showAds || enableTracking) {
          initCMP(props.selfTest.site);
        }

        if (enableTracking) {
          const trackingConfig: TrackingConfig = {
            environment: props.selfTest.environment,
            topic: props.selfTest.slug,
            customer: props.selfTest.customer,
          };

          initTracking(trackingConfig);
        }
      }
    });

    React.useEffect(() => {
      if (
        showAds &&
        props.selfTest &&
        ref.current &&
        cpUserAuthenticatedValue === false &&
        !adIsInitialized
      ) {
        setAdIsInitialized(true);
        initAds(props.selfTest.site);
        setShowCountingAd(true);
      }
    });

    const connectionErrorDiv = (
      <div className="SelfTestView__ConnectionError">
        {showConnectionError && (
          <div>
            <p className="SelfTestView__ConnectionErrorHeading">
              Connection error
            </p>
            <p>
              Please check that you are logged into the admin panel and that the
              edit url parameter refers to a valid self-test slug.
            </p>
          </div>
        )}
      </div>
    );

    return hasConnectionError ? (
      connectionErrorDiv
    ) : (
      <div
        className={cn(
          'SelfTestView',
          'flex flex-col',
          'px-1 md:px-4 pt-2 tall:pt-8',
          props.className
        )}
      >
        <div className={cn('SelfTestView__Card', 'flex flex-col', 'flex-1')}>
          {props.pageIdx !== 0 && (
            <div
              id="SelfTestView__Portal"
              className={cn('flex flex-row', 'px-4 pt-3 pb-2 tall:py-5')}
              ref={(elm) => props.setPortalElm(elm)}
            ></div>
          )}
          {props.question ? (
            <QuestionView className="flex-1" />
          ) : props.ad ? (
            <AdView
              // The key is needed to refresh the AdView if we show two ads in a row.
              // This should never happen, but just in case.
              key={`AdView-${props.pageIdx}`}
              className="flex-1"
              skip={!isAdVisible}
            />
          ) : props.explanation ? (
            <ExplanationView className="flex-1" />
          ) : props.pageIdx === 0 ? (
            <LandingView />
          ) : (
            <SummaryView className="flex-1" />
          )}
        </div>
          {showCountingAd && <div className="SelfTestView__CountingSlot"><AdSlot ad={{position: 'special'}} /></div>}
        <div className={cn('flex flex-row justify-center', 'py-2 tall:py-4')}>
          <img
            alt=""
            src="logo.png"
            className={cn('SelfTestView__Logo', 'grow-0')}
          />
        </div>
      </div>
    );
  })
);
